'use client';

import {
  type DragEvent,
  type MutableRefObject,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import Link from 'next/link';

import PhaserMount from './runtime/PhaserMount';
import {
  createMysteryRoomLocalRuntime,
  moveMysteryRoomInventoryItem,
  setMysteryRoomLocalMoveInput,
  setMysteryRoomLocalPointerTarget,
  type MysteryRoomInventoryItemState,
  type MysteryRoomLocalRuntime,
  type MysteryRoomBotDifficulty,
  type MysteryRoomGameMode,
  type MysteryRoomGlobalMissionKey,
} from './runtime/core/localRuntime';
import { createMysteryRoomRuntimeController } from './runtime/core/runtimeController';
import { updateMysteryRoomLocalRuntime, MYSTERY_ROOM_HERO_RESPAWN_DELAY_MS } from './runtime/core/updateRuntime';
import {
  applyAuthoritativeUsedInteractables,
  applyRemoteP2pDoorToggle,
  applyRemoteP2pInteractableEffect,
  getPrimaryLocalAction,
  hasInventoryFlashlight,
  performPrimaryLocalInteraction,
  performLocalInteraction,
  toggleLocalDoor,
  toggleLocalDoorP2PGuest,
  type MysteryRoomP2pHostGuestStateBundle,
} from './runtime/systems/interactionSystem';
import {
  fireMysteryRoomPistol,
  fireMysteryRoomPistolGuestVisual,
  getMysteryRoomActionBarItems,
  getMysteryRoomDefaultWeaponItem,
  getMysteryRoomFlashlightItem,
  processP2pHostShoot,
  toggleMysteryRoomDefaultWeapon,
  toggleMysteryRoomFlashlight,
  useMysteryRoomActionBarItem,
  type MysteryRoomP2pHostPistolBundle,
  type MysteryRoomP2pPistolContext,
} from './runtime/systems/combatSystem';
import { toggleMysteryRoomHeroCarryObject } from './runtime/systems/movementSystem';
import type { DoorActor } from './runtime/systems/doorSystem';
import { isCircleVisible } from './runtime/systems/visibilitySystem';
import { MYSTERY_ROOM_KEYBOARD_BINDINGS, resolveMysteryRoomActionBarHotkeyIndex } from './runtime/systems/inputSystem';
import {
  MYSTERY_ROOM_BUILD_RECIPES,
  buildMysteryRoomObject,
  getMysteryRoomBuildState,
  getMysteryRoomResourceCounts,
  type MysteryRoomBuildableKind,
  type MysteryRoomResourceCounts,
} from './runtime/systems/buildingSystem';
import { MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN } from './runtime/world/characterAssets';
import type { MysteryRoomMapDefinition } from './runtime/world/mapTypes';
import { MYSTERY_ROOM_RESOURCE_KINDS } from './runtime/world/resourceDefinitions';
import {
  hazardsFromP2pSnapshots,
  mergeExternalRemotePeerBots,
  mergeMrP2pDoorSnapshots,
  mergeMrP2pLightSnapshots,
  mergeMrP2pNpcSnapshots,
  mergeMrP2pTriggerSnapshots,
  mergeP2pWorldAlarm,
} from './runtime/net/p2pStateMerge';
import type {
  MrP2PLightSnapshot,
  MrP2PNpcSnapshot,
  P2pAuthoritativeExtraWorld,
  P2pHostIncomingAction,
} from './runtime/net/p2pTransport';

const LOCAL_LAB_SCALE = 1.5;
const BUILTIN_MAP_ID = 'lisad_9_night_shift';
const LOCAL_CAMERA_FOLLOW_EVENT = 'mystery-room:set-camera-follow';
const MYSTERY_ROOM_AUDIO_SETTINGS_EVENT = 'mystery-room:audio-settings';
const MYSTERY_ROOM_AUDIO_SETTINGS_STORAGE_KEY = 'mysteryRoomAudioSettings';
const DEFAULT_MYSTERY_ROOM_AUDIO_SETTINGS = { musicVolume: 0.3, sfxVolume: 1 };

type MysteryRoomAudioSettings = {
  musicVolume: number;
  sfxVolume: number;
};

type MysteryRoomCoreSidebarTab = 'summary' | 'building' | 'ambientMusic';

type MysteryRoomPerfStats = {
  fps: number;
  uiHz: number;
  frameMs: number;
  updateMs: number;
  customObjects: number;
  interactables: number;
  npcs: number;
  effects: number;
};

const MYSTERY_ROOM_BUILDABLE_KINDS = Object.keys(MYSTERY_ROOM_BUILD_RECIPES) as MysteryRoomBuildableKind[];

function stopGameOverlayPropagation(event: SyntheticEvent<HTMLElement>) {
  event.stopPropagation();
  (event.nativeEvent as Event & { stopImmediatePropagation?: () => void }).stopImmediatePropagation?.();
}

function centerCameraOnPlayer(event: SyntheticEvent<HTMLElement>) {
  stopGameOverlayPropagation(event);
  window.dispatchEvent(new Event('mystery-room:center-camera-on-player'));
}

type Props = {
  preferredSeed: string;
  mapId?: string;
  mapDefinition?: MysteryRoomMapDefinition;
  mapTitle?: string;
  initialRuntime?: MysteryRoomLocalRuntime;
  challengeTarget?: number;
  challengerName?: string;
  // Зачем: позволяет родителю передать число локальных ботов (фолбэк, когда сетевой режим недоступен).
  botCount?: number;
  botsOnly?: boolean;
  gameMode?: MysteryRoomGameMode;
  botDifficulty?: MysteryRoomBotDifficulty;
  globalMission?: MysteryRoomGlobalMissionKey;
  zombieWaveCount?: number;
  zombieWaveSize?: number;
  // Зачем: внешние «акторы» (другие игроки/боты) для P2P-режима — рендерятся как remote-сущности.
  externalBotsRef?: { current: import('./runtime/core/localRuntime').MysteryRoomLocalBotState[] | null };
  /** Снимок NPC с P2P-хоста */
  externalNpcsRef?: { current: MrP2PNpcSnapshot[] | null };
  /** Снимок огней с P2P-хоста */
  externalLightsRef?: { current: MrP2PLightSnapshot[] | null };
  // Зачем: колбэк для P2P-хоста/гостя — даёт доступ к свежему рантайму после каждого тика.
  onRuntimeTick?: (runtime: MysteryRoomLocalRuntime, dtMs: number) => void;
  /** P2P-хост: очередь действий гостей */
  p2pHostInteractQueueRef?: MutableRefObject<P2pHostIncomingAction[]>;
  /** P2P-гость: последний список usedInteractableIds с хоста (null — ещё не было state) */
  p2pAuthoritativeUsedInteractableIdsRef?: MutableRefObject<string[] | null>;
  /** P2P-гость: двери, триггеры, hazards и тревога с хоста */
  p2pAuthoritativeExtraWorldRef?: MutableRefObject<P2pAuthoritativeExtraWorld | null>;
  /** P2P-гость: не крутить локальные триггеры — мир с хоста */
  p2pSkipLocalTriggers?: boolean;
  /** P2P-гость: уведомить хоста о взаимодействии (подбор, выключатель) */
  onP2pGuestInteractableUsed?: (interactableId: string) => void;
  /** P2P-гость: дверь с клиента (клик/E) */
  onP2pGuestDoorToggled?: (doorId: string) => void;
  /** P2P-хост: bundle для processP2pHostShoot (очередь выстрелов) */
  p2pHostPistolBundleRef?: MutableRefObject<MysteryRoomP2pHostPistolBundle | null>;
  /** P2P-гость: HP/alive с state хоста */
  p2pAuthoritativeHeroRef?: MutableRefObject<{ health: number; maxHealth: number; alive: boolean } | null>;
  /** P2P-гость: клик по выстрелу — только визуал, событие на хоста */
  onP2pGuestShoot?: (targetX: number, targetY: number) => void;
  /** P2P-хост: инвентари гостей и тени (очередь interact) */
  p2pHostGuestStateBundleRef?: MutableRefObject<MysteryRoomP2pHostGuestStateBundle | null>;
  /** P2P-хост: акторы дверей из теней гостей (карта/пропуска); обновляется в onRuntimeTick */
  p2pHostExtraDoorActorsRef?: MutableRefObject<DoorActor[]>;
  /** P2P-гость: инвентарь с хоста (после inventory_init + state) */
  p2pAuthoritativeGuestInventoryRef?: MutableRefObject<MysteryRoomInventoryItemState[] | null>;
  /** Роль: гость — выстрел/merge дверей */
  p2pIsGuest?: boolean;
};

type LocalChatMessage = {
  id: string;
  author: string;
  body: string;
  createdAtMs: number;
};

export function shouldHandlePrimaryInteractionHotkeyTarget(target: EventTarget | null) {
  if (!target || typeof target !== 'object') {
    return true;
  }

  const candidate = target as {
    isContentEditable?: boolean;
    closest?: (selector: string) => Element | null | undefined;
  };

  if (candidate.isContentEditable) {
    return false;
  }

  if (typeof candidate.closest === 'function') {
    return !candidate.closest('button, a, input, textarea, select, [contenteditable="true"]');
  }

  return true;
}

function notifyP2pGuestInteractableUsedFromPrimary(
  beforeAction: ReturnType<typeof getPrimaryLocalAction>,
  current: MysteryRoomLocalRuntime,
  updated: MysteryRoomLocalRuntime,
  onP2pGuestInteractableUsed?: (id: string) => void,
) {
  if (!onP2pGuestInteractableUsed || beforeAction?.kind !== 'interactable') {
    return;
  }
  const tid = beforeAction.targetId;
  const it = current.interactables.find((e) => e.id === tid);
  if (it?.kind === 'light_switch') {
    if (updated !== current) onP2pGuestInteractableUsed(tid);
    return;
  }
  const prev = it?.state;
  const aft = updated.interactables.find((e) => e.id === tid)?.state;
  if (prev !== 'used' && aft === 'used') {
    onP2pGuestInteractableUsed(tid);
  }
}

function notifyP2pGuestDoorFromPrimary(
  beforeAction: ReturnType<typeof getPrimaryLocalAction>,
  current: MysteryRoomLocalRuntime,
  updated: MysteryRoomLocalRuntime,
  onP2pGuestDoorToggled?: (doorId: string) => void,
) {
  if (!onP2pGuestDoorToggled || beforeAction?.kind !== 'door') {
    return;
  }
  if (updated !== current) {
    onP2pGuestDoorToggled(beforeAction.targetId);
  }
}

export function resolveMysteryRoomCoreLabScale(mapId?: string, mapDefinition?: MysteryRoomMapDefinition): number {
  const effectiveMapId = mapDefinition?.id ?? mapId ?? BUILTIN_MAP_ID;
  return effectiveMapId === BUILTIN_MAP_ID ? LOCAL_LAB_SCALE : 1;
}

export default function MysteryRoomCoreSlice({
  preferredSeed,
  mapId,
  mapDefinition,
  mapTitle,
  initialRuntime,
  challengeTarget,
  challengerName,
  botCount,
  botsOnly,
  gameMode,
  botDifficulty,
  globalMission,
  zombieWaveCount,
  zombieWaveSize,
  externalBotsRef,
  externalNpcsRef,
  externalLightsRef,
  onRuntimeTick,
  p2pHostInteractQueueRef,
  p2pAuthoritativeUsedInteractableIdsRef,
  p2pAuthoritativeExtraWorldRef,
  p2pSkipLocalTriggers,
  onP2pGuestInteractableUsed,
  onP2pGuestDoorToggled,
  p2pHostPistolBundleRef,
  p2pAuthoritativeHeroRef,
  onP2pGuestShoot,
  p2pIsGuest,
  p2pHostGuestStateBundleRef,
  p2pHostExtraDoorActorsRef,
  p2pAuthoritativeGuestInventoryRef,
}: Props) {
  const createRuntime = useCallback(() =>
    initialRuntime ??
    createMysteryRoomLocalRuntime(mapId ?? 'lisad_9_night_shift', {
      labScale: resolveMysteryRoomCoreLabScale(mapId, mapDefinition),
      mapDefinition,
      botCount,
      botsOnly,
      gameMode,
      botDifficulty,
      globalMission,
      zombieWaveCount,
      zombieWaveSize,
    }), [
      initialRuntime,
      mapId,
      mapDefinition,
      botCount,
      botsOnly,
      gameMode,
      botDifficulty,
      globalMission,
      zombieWaveCount,
      zombieWaveSize,
    ]);
  const runtimeControllerRef = useRef<ReturnType<typeof createMysteryRoomRuntimeController> | null>(null);
  if (runtimeControllerRef.current === null) {
    runtimeControllerRef.current = createMysteryRoomRuntimeController(createRuntime());
  }
  const runtimeController = runtimeControllerRef.current;
  const runtimeSnapshot = useSyncExternalStore(
    runtimeController.subscribe,
    runtimeController.getSnapshot,
    runtimeController.getSnapshot,
  );
  const runtime = runtimeSnapshot.runtime;
  const [chatDraft, setChatDraft] = useState('');
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [cameraFollowHero, setCameraFollowHero] = useState(true);
  const [audioSettings, setAudioSettings] = useState<MysteryRoomAudioSettings>(() => readMysteryRoomAudioSettings());
  const [sidebarTab, setSidebarTab] = useState<MysteryRoomCoreSidebarTab>('summary');
  const [localChat, setLocalChat] = useState<LocalChatMessage[]>(() => [
    {
      id: 'system-briefing',
      author: 'Смена ЛИСАД',
      body: 'Core Slice активен: используйте чат для заметок, ложных следов и координации перед серверным матчем.',
      createdAtMs: 0,
    },
  ]);
  const perfOverlayEnabled = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('perf') === '1';
  }, []);
  const [perfStats, setPerfStats] = useState<MysteryRoomPerfStats | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastFrameRef = useRef<number | null>(null);
  const lastUiPublishRef = useRef<number>(0);
  const perfAccumulatorRef = useRef({ startMs: 0, frames: 0, frameMs: 0, updateMs: 0, uiPublishes: 0 });
  const publishRuntimeUi = useCallback(
    (now = typeof performance === 'undefined' ? Date.now() : performance.now()) => {
      runtimeController.publish();
      lastUiPublishRef.current = now;
      perfAccumulatorRef.current.uiPublishes += 1;
    },
    [runtimeController],
  );
  const applyRuntimeAction = useCallback(
    (
      updater: (runtime: MysteryRoomLocalRuntime) => MysteryRoomLocalRuntime,
      options: { publish?: boolean } = {},
    ) => {
      const next = runtimeController.mutate(updater, { publish: false });
      if (options.publish !== false) {
        publishRuntimeUi();
      }
      return next;
    },
    [publishRuntimeUi, runtimeController],
  );

  useEffect(() => {
    // Зачем: число ботов из лобби приходит/уточняется после P2P (null→host или 0→N); без deps рантайм остаётся без ИИ.
    runtimeController.replace(createRuntime(), { publish: false });
    lastFrameRef.current = null;
    lastUiPublishRef.current = 0;
    publishRuntimeUi();
    setChatDraft('');
    setInventoryOpen(false);
    setCameraFollowHero(true);
    setLocalChat([
      {
        id: 'system-briefing',
        author: 'Смена ЛИСАД',
        body: 'Core Slice активен: используйте чат для заметок, ложных следов и координации перед серверным матчем.',
        createdAtMs: 0,
      },
    ]);
  }, [createRuntime, preferredSeed, publishRuntimeUi, runtimeController]);

  useEffect(() => {
    if (runtime.gameStatus === 'respawning') setInventoryOpen(false);
  }, [runtime.gameStatus]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent(LOCAL_CAMERA_FOLLOW_EVENT, { detail: { enabled: cameraFollowHero } }));
  }, [cameraFollowHero]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const normalized: MysteryRoomAudioSettings = {
      musicVolume: clampMysteryRoomVolume(audioSettings.musicVolume),
      sfxVolume: clampMysteryRoomVolume(audioSettings.sfxVolume),
    };
    try {
      window.localStorage.setItem(MYSTERY_ROOM_AUDIO_SETTINGS_STORAGE_KEY, JSON.stringify(normalized));
    } catch {
      // Настройки громкости не критичны для запуска игры.
    }
    window.dispatchEvent(new CustomEvent(MYSTERY_ROOM_AUDIO_SETTINGS_EVENT, { detail: normalized }));
  }, [audioSettings]);

  useEffect(() => {
    const tick = (now: number) => {
      const last = lastFrameRef.current ?? now;
      const dtMs = Math.min(40, now - last);
      lastFrameRef.current = now;
      let measuredUpdateMs = 0;
      runtimeController.mutate((current) => {
        const updateStartedAt = perfOverlayEnabled ? performance.now() : 0;
        let working = current;
        const q = p2pHostInteractQueueRef?.current;
        if (q?.length) {
          const batch = q.splice(0, q.length);
          for (const item of batch) {
            if (item.fromPeerId != null && typeof item.targetX === 'number' && typeof item.targetY === 'number') {
              const bundle = p2pHostPistolBundleRef?.current;
              if (bundle?.hostPeerId) {
                const ctx: MysteryRoomP2pPistolContext = {
                  hostPeerId: bundle.hostPeerId,
                  shooterPeerId: item.fromPeerId,
                  guestShadows: bundle.guestShadowHeroes,
                  lastShotMsByPeer: bundle.lastShotMsByPeer,
                };
                working = processP2pHostShoot(
                  working,
                  {
                    fromPeerId: item.fromPeerId,
                    targetX: item.targetX,
                    targetY: item.targetY,
                    tsMs: item.tsMs ?? 0,
                  },
                  ctx,
                );
              }
              continue;
            }
            if (item.doorId) {
              working = applyRemoteP2pDoorToggle(working, item.doorId);
            }
            if (item.interactableId) {
              const gb = p2pHostGuestStateBundleRef?.current;
              const guestCtx =
                item.guestPeerId && gb
                  ? {
                      guestPeerId: item.guestPeerId,
                      guestInventories: gb.guestInventories,
                      guestShadows: gb.guestShadowHeroes,
                    }
                  : undefined;
              working = applyRemoteP2pInteractableEffect(working, item.interactableId, guestCtx);
            }
          }
        }
        let next = updateMysteryRoomLocalRuntime(working, dtMs, {
          skipLocalTriggers: !!p2pSkipLocalTriggers,
          extraDoorActors: p2pHostExtraDoorActorsRef?.current,
        });
        const externalBots = externalBotsRef?.current;
        if (externalBots != null) {
          const simOnly = (next.localBots ?? []).filter((b) => !b.remotePeer);
          const merged = mergeExternalRemotePeerBots(next.localBots, externalBots);
          next = { ...next, localBots: [...merged, ...simOnly] };
        }
        const authUsed = p2pAuthoritativeUsedInteractableIdsRef?.current;
        if (authUsed != null) {
          next = applyAuthoritativeUsedInteractables(next, authUsed);
        }
        const extNpcs = externalNpcsRef?.current ?? null;
        if (extNpcs && extNpcs.length > 0) {
          next = { ...next, npcs: mergeMrP2pNpcSnapshots(next.npcs, extNpcs) };
        }
        const extLights = externalLightsRef?.current ?? null;
        if (extLights && extLights.length > 0) {
          next = { ...next, lights: mergeMrP2pLightSnapshots(next.lights, extLights) };
        }
        const extraWorld = p2pAuthoritativeExtraWorldRef?.current ?? null;
        if (extraWorld) {
          if (extraWorld.doors.length > 0) {
            next = {
              ...next,
              doors: mergeMrP2pDoorSnapshots(next.doors, extraWorld.doors, p2pSkipLocalTriggers ? { guestCatchUp: true } : undefined),
            };
          }
          if (extraWorld.triggers.length > 0) {
            next = { ...next, triggers: mergeMrP2pTriggerSnapshots(next.triggers, extraWorld.triggers) };
          }
          next = { ...next, hazards: hazardsFromP2pSnapshots(extraWorld.hazards) };
          next = { ...next, worldAlarm: mergeP2pWorldAlarm(next.worldAlarm, extraWorld.worldAlarm) };
        }
        const authHero = p2pAuthoritativeHeroRef?.current;
        if (authHero && p2pSkipLocalTriggers) {
          next = {
            ...next,
            hero: {
              ...next.hero,
              health: authHero.health,
              maxHealth: authHero.maxHealth,
              alive: authHero.alive,
            },
          };
        }
        const authGuestInv = p2pAuthoritativeGuestInventoryRef?.current;
        if (authGuestInv != null && p2pSkipLocalTriggers) {
          next = { ...next, inventory: authGuestInv.map((item, slotIndex) => ({ ...item, slotIndex })) };
        }
        onRuntimeTick?.(next, dtMs);
        if (perfOverlayEnabled) {
          measuredUpdateMs = performance.now() - updateStartedAt;
        }
        return next;
      }, { publish: false });
      if (lastUiPublishRef.current === 0 || now - lastUiPublishRef.current >= 100) {
        publishRuntimeUi(now);
      }
      if (perfOverlayEnabled) {
        const acc = perfAccumulatorRef.current;
        if (acc.startMs === 0) {
          acc.startMs = now;
        }
        acc.frames += 1;
        acc.frameMs += dtMs;
        acc.updateMs += measuredUpdateMs;
        if (now - acc.startMs >= 500) {
          const current = runtimeController.getRuntime();
          setPerfStats({
            fps: Math.round((acc.frames * 1000) / Math.max(1, now - acc.startMs)),
            uiHz: Math.round((acc.uiPublishes * 1000) / Math.max(1, now - acc.startMs)),
            frameMs: Math.round((acc.frameMs / Math.max(1, acc.frames)) * 10) / 10,
            updateMs: Math.round((acc.updateMs / Math.max(1, acc.frames)) * 10) / 10,
            customObjects: current.map.customObjects.length,
            interactables: current.interactables.length,
            npcs: current.npcs.filter((npc) => npc.alive).length,
            effects: current.impactEffects.length + current.hazards.length,
          });
          perfAccumulatorRef.current = { startMs: now, frames: 0, frameMs: 0, updateMs: 0, uiPublishes: 0 };
        }
      }
      rafRef.current = window.requestAnimationFrame(tick);
    };

    rafRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
      }
      lastFrameRef.current = null;
    };
  }, [perfOverlayEnabled, p2pSkipLocalTriggers, publishRuntimeUi, runtimeController]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (runtimeController.getRuntime().gameStatus === 'respawning') {
        return;
      }
      if (
        event.code === MYSTERY_ROOM_KEYBOARD_BINDINGS.inventory &&
        !event.repeat &&
        shouldHandlePrimaryInteractionHotkeyTarget(event.target)
      ) {
        event.preventDefault();
        setInventoryOpen((current) => !current);
        return;
      }

      if (event.repeat || !shouldHandlePrimaryInteractionHotkeyTarget(event.target)) {
        return;
      }

      if (event.code === MYSTERY_ROOM_KEYBOARD_BINDINGS.primaryInteract) {
        applyRuntimeAction((current) => {
          const beforeAction = getPrimaryLocalAction(current);
          const updated = performPrimaryLocalInteraction(current, { p2pGuestDoors: Boolean(onP2pGuestDoorToggled) });
          notifyP2pGuestInteractableUsedFromPrimary(beforeAction, current, updated, onP2pGuestInteractableUsed);
          notifyP2pGuestDoorFromPrimary(beforeAction, current, updated, onP2pGuestDoorToggled);
          return updated;
        });
        return;
      }

      if (event.code === MYSTERY_ROOM_KEYBOARD_BINDINGS.carry) {
        applyRuntimeAction((current) => toggleMysteryRoomHeroCarryObject(current));
        return;
      }

      if (event.code === MYSTERY_ROOM_KEYBOARD_BINDINGS.weapon) {
        applyRuntimeAction((current) => toggleMysteryRoomDefaultWeapon(current));
        return;
      }

      if (event.code === MYSTERY_ROOM_KEYBOARD_BINDINGS.flashlight) {
        applyRuntimeAction((current) => toggleMysteryRoomFlashlight(current));
        return;
      }

      const actionBarIndex = resolveMysteryRoomActionBarHotkeyIndex(event.code);
      if (actionBarIndex !== null) {
        applyRuntimeAction((current) => {
          const item = getMysteryRoomActionBarItems(current)[actionBarIndex];
          return item ? useMysteryRoomActionBarItem(current, item.id) : current;
        });
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [applyRuntimeAction, onP2pGuestInteractableUsed, onP2pGuestDoorToggled, runtimeController]);

  const heroZone = useMemo(() => runtime.map.rooms.find((room) => room.id === runtime.hero.zoneId), [runtime.hero.zoneId, runtime.map.rooms]);
  const visibleNpcCount = useMemo(() => {
    if (!runtime.visibility) {
      return 0;
    }
    return runtime.npcs.filter((npc) => isCircleVisible(runtime.visibility, npc.position, npc.radius)).length;
  }, [runtime.npcs, runtime.visibility]);
  const openDoors = useMemo(() => runtime.doors.filter((door) => door.state === 'open').length, [runtime.doors]);
  const enabledLights = useMemo(() => runtime.lights.filter((light) => light.enabled).length, [runtime.lights]);
  const defaultWeaponItem = useMemo(() => getMysteryRoomDefaultWeaponItem(runtime), [runtime]);
  const flashlightItem = useMemo(() => getMysteryRoomFlashlightItem(runtime), [runtime]);
  const actionBarItems = useMemo(() => getMysteryRoomActionBarItems(runtime), [runtime]);
  const resourceCounts = useMemo(() => getMysteryRoomResourceCounts(runtime), [runtime]);
  const buildStates = useMemo(
    () => ({
      barricade: getMysteryRoomBuildState(runtime, 'barricade'),
      resource_crate: getMysteryRoomBuildState(runtime, 'resource_crate'),
    }),
    [runtime],
  );
  const serverHref = useMemo(() => {
    const params = new URLSearchParams();
    params.set('runtime', 'server');
    if (mapId && mapId !== 'lisad_9_night_shift') {
      params.set('mapId', mapId);
    }
    return `/games/mystery-room?${params.toString()}`;
  }, [mapId]);
  const sendLocalChatMessage = () => {
    const body = chatDraft.trim();
    if (!body) {
      return;
    }

    setLocalChat((current) =>
      [
        ...current,
        {
          id: `local-chat-${Date.now()}-${current.length}`,
          author: 'Оператор',
          body: body.slice(0, 240),
          createdAtMs: runtimeController.getRuntime().elapsedMs,
        },
      ].slice(-12),
    );
    setChatDraft('');
  };

  if (typeof window !== 'undefined') {
    const gameWindow = window as typeof window & {
      render_game_to_text?: () => string;
      advanceTime?: (ms?: number) => void;
      __mysteryRoomAudioDebug?: unknown;
      __mysteryRoomCameraDebug?: unknown;
    };

    gameWindow.render_game_to_text = () => {
      const current = runtimeController.getRuntime();
      return JSON.stringify(
        buildMysteryRoomTextState(
          current,
          localChat,
          gameWindow.__mysteryRoomAudioDebug ?? null,
          gameWindow.__mysteryRoomCameraDebug ?? null,
        ),
      );
    };

    gameWindow.advanceTime = (ms = 16) => {
      const stepMs = 1000 / 60;
      const steps = Math.max(1, Math.ceil(ms / stepMs));
      applyRuntimeAction((current) => {
        let next = current;
        for (let index = 0; index < steps; index += 1) {
          next = updateMysteryRoomLocalRuntime(next, stepMs);
        }
        return next;
      }, { publish: false });
      const now = performance.now();
      if (lastUiPublishRef.current === 0 || now - lastUiPublishRef.current >= 100) {
        publishRuntimeUi(now);
      }
    };
  }

  return (
    <section className="h-full w-full overflow-hidden bg-[radial-gradient(circle_at_top,#1f2937_0%,#050816_42%,#020617_100%)] p-3 sm:p-4">
      <div className="grid h-full min-h-0 gap-3 xl:grid-cols-[300px_minmax(0,1fr)_320px]">
        <aside className="flex min-h-0 flex-col gap-3 overflow-y-auto rounded-[24px] border border-site-border bg-site-bg/82 p-4">
          <div>
            <div className="text-[0.68rem] uppercase tracking-[0.24em] text-site-accent/80">Core Slice</div>
            <h1 className="mt-2 text-2xl font-black text-site-text">Mystery Room</h1>
            <p className="mt-2 text-sm leading-6 text-site-secondary">
              Локальный Phaser-срез лаборатории: крупная карта, ручные двери, свет, инвентарь, NPC-патруль и `fog of war`.
            </p>
            <div className="mt-3 rounded-[14px] border border-site-accent/18 bg-site-accent/8 px-3 py-2 text-sm text-site-accent">
              {mapTitle ?? runtime.map.title}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <Metric label="Сектор" value={heroZone?.title ?? runtime.hero.zoneId} />
            <Metric label="Двери open" value={openDoors} />
            <Metric label="NPC в LOS" value={visibleNpcCount} />
            <Metric label="Свет" value={`${enabledLights}/${runtime.lights.length}`} />
            <Metric label="Scale" value={`${LOCAL_LAB_SCALE}x`} />
            <Metric label="Alert" value={runtime.hero.alertState} />
            <Metric label="Objects" value={runtime.map.customObjects.length} />
            <Metric label="Triggers" value={runtime.triggers.length} />
          </div>

          <Panel title="Управление">
            <ul className="space-y-2 text-sm text-site-secondary">
              <li>`WASD` / стрелки: ручное движение</li>
              <li>ЛКМ: `click-to-move`</li>
              <li>ЛКМ с активным пистолетом: выстрел в курсор</li>
              <li>`E`: двери, свет, предметы, NPC</li>
              <li>`F`: взять/отпустить физический предмет перед героем</li>
              <li>`Q`: достать/убрать выбранное оружие</li>
              <li>`T`: включить/выключить фонарик</li>
              <li>`1-5`: использовать быстрые слоты инвентаря</li>
              <li>Ручной ввод всегда отменяет click target</li>
              <li>Закрытые двери режут проход и обзор</li>
            </ul>
          </Panel>

          {typeof challengeTarget === 'number' && (
            <Panel title="Daily Challenge">
              <div className="rounded-[14px] border border-site-warning/20 bg-site-warning/8 px-3 py-2 text-sm leading-6 text-site-warning">
                {challengerName ? `${challengerName} бросил вызов:` : 'Результат-вызов:'} побей{' '}
                <span className="font-bold">{challengeTarget}</span>.
              </div>
            </Panel>
          )}

          <Panel title="Карта">
            <div className="space-y-2 text-sm text-site-secondary">
              {runtime.map.rooms.map((room) => (
                <div key={room.id} className="flex items-center justify-between rounded-[14px] border border-site-border bg-site-surface/[0.03] px-3 py-2">
                  <span>{room.title}</span>
                  <span className={`text-[0.62rem] uppercase tracking-[0.16em] ${room.id === runtime.hero.zoneId ? 'text-site-warning' : 'text-site-muted'}`}>
                    {room.id === runtime.hero.zoneId ? 'здесь' : room.accessLevel}
                  </span>
                </div>
              ))}
            </div>
          </Panel>

          <div className="mt-auto flex flex-wrap gap-2">
            <Link
              href={serverHref}
              className="rounded-full border border-site-border bg-site-surface/[0.04] px-4 py-2 text-sm text-site-text transition hover:border-site-border"
            >
              server
            </Link>
          </div>
        </aside>

        <div className="relative min-h-0">
          <PhaserMount
            mode="local"
            localRuntime={runtime}
            getLocalRuntime={runtimeController.getRuntime}
            onLocalMoveVectorChange={(x, y) => {
              applyRuntimeAction((current) => setMysteryRoomLocalMoveInput(current, { x, y }), { publish: false });
            }}
            onLocalPointerTarget={(x, y) => {
              applyRuntimeAction((current) => setMysteryRoomLocalPointerTarget(current, { x, y }), { publish: false });
            }}
            onLocalInteract={(rawId) => {
              applyRuntimeAction((current) => {
                if (rawId.startsWith('door:')) {
                  const doorId = rawId.slice('door:'.length);
                  const updated = onP2pGuestDoorToggled ? toggleLocalDoorP2PGuest(current, doorId) : toggleLocalDoor(current, doorId);
                  if (onP2pGuestDoorToggled && updated !== current) {
                    onP2pGuestDoorToggled(doorId);
                  }
                  return updated;
                }
                const it = current.interactables.find((e) => e.id === rawId);
                const prev = it?.state;
                const updated = performLocalInteraction(current, rawId);
                const aft = updated.interactables.find((e) => e.id === rawId)?.state;
                if (onP2pGuestInteractableUsed) {
                  if (it?.kind === 'light_switch') {
                    if (updated !== current) onP2pGuestInteractableUsed(rawId);
                  } else if (prev !== 'used' && aft === 'used') {
                    onP2pGuestInteractableUsed(rawId);
                  }
                }
                return updated;
              });
            }}
            onLocalShoot={(x, y) => {
              applyRuntimeAction((current) => {
                if (p2pIsGuest) {
                  onP2pGuestShoot?.(x, y);
                  return fireMysteryRoomPistolGuestVisual(current, { x, y });
                }
                const bundle = p2pHostPistolBundleRef?.current;
                if (bundle?.hostPeerId) {
                  const ctx: MysteryRoomP2pPistolContext = {
                    hostPeerId: bundle.hostPeerId,
                    shooterPeerId: bundle.hostPeerId,
                    guestShadows: bundle.guestShadowHeroes,
                    lastShotMsByPeer: bundle.lastShotMsByPeer,
                  };
                  return fireMysteryRoomPistol(current, { x, y }, { p2p: ctx });
                }
                return fireMysteryRoomPistol(current, { x, y });
              });
            }}
          />
          <div className="pointer-events-none absolute inset-x-4 top-4 z-20 flex flex-wrap items-start justify-between gap-3">
            {runtime.gameStatus === 'respawning' ? (
              <div className="pointer-events-auto flex min-w-0 max-w-[min(100%,28rem)] flex-1 flex-wrap items-center gap-3 rounded-[14px] border border-site-danger/35 bg-site-bg/92 px-4 py-2.5 shadow-[0_12px_40px_rgba(0,0,0,0.38)]">
                <div className="min-w-0 flex-1 text-left">
                  <div className="text-[0.62rem] font-bold uppercase tracking-[0.2em] text-site-danger/85">Возрождение</div>
                  <div className="mt-0.5 text-lg font-black leading-tight text-site-text">Герой погиб</div>
                  <div className="mt-1 text-[0.8rem] leading-snug text-site-secondary">
                    На контрольной точке через{' '}
                    {Math.max(
                      0,
                      Math.ceil(
                        ((runtime.respawn?.readyAtMs ?? runtime.elapsedMs + MYSTERY_ROOM_HERO_RESPAWN_DELAY_MS) -
                          runtime.elapsedMs) /
                          1000,
                      ),
                    )}{' '}
                    сек. · наблюдаете место гибели
                  </div>
                </div>
                <button
                  type="button"
                  onPointerDown={stopGameOverlayPropagation}
                  onPointerUp={stopGameOverlayPropagation}
                  onMouseDown={stopGameOverlayPropagation}
                  onMouseUp={stopGameOverlayPropagation}
                  onClick={(event) => {
                    stopGameOverlayPropagation(event);
                    setInventoryOpen(false);
                    runtimeController.replace(
                      createMysteryRoomLocalRuntime(runtime.map.id, {
                        mapDefinition: runtime.map,
                        botCount: runtime.localBots.filter((bot) => !bot.remotePeer).length,
                        gameMode: runtime.gameplay.gameMode,
                        botDifficulty: runtime.gameplay.botDifficulty,
                        globalMission: runtime.globalMission.key,
                        zombieWaveCount: runtime.zombieMode.totalWaves,
                        zombieWaveSize: runtime.zombieMode.waveSize,
                      }),
                      { publish: false },
                    );
                    publishRuntimeUi();
                  }}
                  className="shrink-0 rounded-[12px] border border-site-accent/35 bg-site-accent/14 px-3 py-2 text-[0.8rem] font-bold text-site-accent transition hover:border-site-accent/55 hover:bg-site-accent/22"
                >
                  Начать заново
                </button>
              </div>
            ) : (
              <span className="min-w-[4px] flex-1 shrink" aria-hidden />
            )}
            <div className={`pointer-events-none flex shrink-0 flex-wrap justify-end gap-2 ${runtime.gameStatus === 'respawning' ? 'opacity-35' : ''}`}>
              <div className="pointer-events-auto flex min-h-[38px] items-center gap-2 rounded-[14px] border border-site-border bg-site-bg/88 px-3 py-2 text-xs font-semibold text-site-text shadow-[0_12px_32px_rgba(2,6,23,0.38)] backdrop-blur">
                <span className="text-[0.62rem] uppercase tracking-[0.16em] text-site-muted">Ресурсы</span>
                <span className="rounded-[8px] bg-site-surface/[0.05] px-2 py-1 text-site-text">Д {resourceCounts.wood}</span>
                <span className="rounded-[8px] bg-site-surface/[0.05] px-2 py-1 text-site-text">М {resourceCounts.metal}</span>
                <span className="rounded-[8px] bg-site-surface/[0.05] px-2 py-1 text-site-text">К {resourceCounts.stone}</span>
              </div>
              <button
                type="button"
                disabled={runtime.gameStatus === 'respawning'}
                aria-pressed={cameraFollowHero}
                onPointerDown={stopGameOverlayPropagation}
                onPointerUp={stopGameOverlayPropagation}
                onMouseDown={stopGameOverlayPropagation}
                onMouseUp={stopGameOverlayPropagation}
                onClick={(event) => {
                  stopGameOverlayPropagation(event);
                  setCameraFollowHero((current) => !current);
                }}
                className={`pointer-events-auto rounded-[14px] border px-4 py-2 text-sm font-semibold shadow-[0_12px_32px_rgba(2,6,23,0.38)] backdrop-blur transition disabled:cursor-not-allowed disabled:opacity-55 ${
                  cameraFollowHero
                    ? 'border-site-success/35 bg-site-success/14 text-site-success hover:border-site-success/55 hover:bg-site-success/18'
                    : 'border-site-border bg-site-bg/88 text-site-text hover:border-site-border hover:bg-site-surface/[0.08]'
                }`}
              >
                {cameraFollowHero ? 'Камера: герой' : 'Камера: ручная'}
              </button>
              <button
                type="button"
                disabled={runtime.gameStatus === 'respawning'}
                onPointerDown={stopGameOverlayPropagation}
                onPointerUp={stopGameOverlayPropagation}
                onMouseDown={stopGameOverlayPropagation}
                onMouseUp={stopGameOverlayPropagation}
                onClick={centerCameraOnPlayer}
                className="pointer-events-auto rounded-[14px] border border-site-border bg-site-bg/88 px-4 py-2 text-sm font-semibold text-site-text shadow-[0_12px_32px_rgba(2,6,23,0.38)] backdrop-blur transition hover:border-site-border hover:bg-site-surface/[0.08] disabled:cursor-not-allowed disabled:opacity-55"
              >
                К игроку
              </button>
              <button
                type="button"
                disabled={runtime.gameStatus === 'respawning'}
                onPointerDown={stopGameOverlayPropagation}
                onPointerUp={stopGameOverlayPropagation}
                onMouseDown={stopGameOverlayPropagation}
                onMouseUp={stopGameOverlayPropagation}
                onClick={(event) => {
                  stopGameOverlayPropagation(event);
                  setInventoryOpen((current) => !current);
                }}
                className="pointer-events-auto rounded-[14px] border border-site-accent/25 bg-site-bg/88 px-4 py-2 text-sm font-semibold text-site-accent shadow-[0_12px_32px_rgba(2,6,23,0.38)] backdrop-blur transition hover:border-site-accent/45 hover:bg-site-accent/12 disabled:cursor-not-allowed disabled:opacity-55"
              >
                {inventoryOpen ? 'Закрыть инвентарь' : 'Инвентарь'}
              </button>
            </div>
          </div>
          {perfOverlayEnabled && perfStats ? (
            <div className="pointer-events-none absolute right-4 top-20 z-20 w-[210px] rounded-[12px] border border-site-success/24 bg-site-bg/88 px-3 py-2 font-mono text-[0.68rem] text-site-success shadow-[0_12px_32px_rgba(2,6,23,0.4)]">
              <div className="font-black uppercase tracking-[0.14em] text-site-success">perf</div>
              <div className="mt-1 grid grid-cols-2 gap-x-2 gap-y-0.5">
                <span>fps</span><span className="text-right">{perfStats.fps}</span>
                <span>uiHz</span><span className="text-right">{perfStats.uiHz}</span>
                <span>frame</span><span className="text-right">{perfStats.frameMs}ms</span>
                <span>update</span><span className="text-right">{perfStats.updateMs}ms</span>
                <span>objects</span><span className="text-right">{perfStats.customObjects}</span>
                <span>interact</span><span className="text-right">{perfStats.interactables}</span>
                <span>npc</span><span className="text-right">{perfStats.npcs}</span>
                <span>effects</span><span className="text-right">{perfStats.effects}</span>
              </div>
            </div>
          ) : null}
          {inventoryOpen ? (
            <InventoryOverlay
              runtime={runtime}
              onClose={() => setInventoryOpen(false)}
              onMoveItem={(itemId, beforeItemId) => {
                applyRuntimeAction((current) => moveMysteryRoomInventoryItem(current, itemId, beforeItemId));
              }}
              activeItemId={runtime.activeInventoryItemId}
              onToggleActiveItem={(itemId) => {
                applyRuntimeAction((current) => useMysteryRoomActionBarItem(current, itemId));
              }}
            />
          ) : null}
          {runtime.gameStatus === 'game_over' && runtime.globalMission.completed ? (
            <div className="pointer-events-auto absolute inset-0 z-40 flex items-center justify-center bg-site-success/10 backdrop-blur-sm">
              <div className="w-[min(460px,calc(100%-32px))] rounded-[18px] border border-site-success/35 bg-site-bg/92 p-5 text-center shadow-[0_24px_80px_rgba(0,0,0,0.5)]">
                <div className="text-[0.7rem] uppercase tracking-[0.22em] text-site-success/85">Операция завершена</div>
                <div className="mt-2 text-2xl font-black text-site-text">{runtime.globalMission.title}</div>
                <div className="mt-2 text-sm leading-6 text-site-secondary">{runtime.globalMission.description}</div>
                <div className="mt-3 rounded-[12px] border border-site-success/20 bg-site-success/10 px-3 py-2 text-sm text-site-success">
                  Награда за миссию: +{runtime.globalMission.rewardMoney} CR
                </div>
                <button
                  type="button"
                  onPointerDown={stopGameOverlayPropagation}
                  onPointerUp={stopGameOverlayPropagation}
                  onMouseDown={stopGameOverlayPropagation}
                  onMouseUp={stopGameOverlayPropagation}
                  onClick={(event) => {
                    stopGameOverlayPropagation(event);
                    runtimeController.replace(
                      createMysteryRoomLocalRuntime(runtime.map.id, {
                        mapDefinition: runtime.map,
                        botCount: runtime.localBots.filter((bot) => !bot.remotePeer).length,
                        gameMode: runtime.gameplay.gameMode,
                        botDifficulty: runtime.gameplay.botDifficulty,
                        globalMission: runtime.globalMission.key,
                        zombieWaveCount: runtime.zombieMode.totalWaves,
                        zombieWaveSize: runtime.zombieMode.waveSize,
                      }),
                      { publish: false },
                    );
                    publishRuntimeUi();
                  }}
                  className="mt-4 rounded-[14px] border border-site-success/35 bg-site-success/14 px-4 py-2 text-sm font-bold text-site-success transition hover:border-site-success/55 hover:bg-site-success/20"
                >
                  Повторить миссию
                </button>
              </div>
            </div>
          ) : null}
          <div className="pointer-events-none absolute inset-x-4 bottom-12 z-10">
            <div
              className={`pointer-events-auto mx-auto flex w-full max-w-[1040px] flex-nowrap items-center gap-2 overflow-x-auto rounded-[18px] border border-site-border bg-site-bg/86 px-3 py-3 shadow-[0_12px_48px_rgba(2,6,23,0.45)] backdrop-blur ${runtime.gameStatus === 'respawning' ? '!pointer-events-none opacity-35' : ''}`}
            >
              <ActionBarSlot
                hotkey="Q"
                item={defaultWeaponItem}
                active={defaultWeaponItem?.id === runtime.activeInventoryItemId}
                emptyLabel="Оружие"
                onClick={() => applyRuntimeAction((current) => toggleMysteryRoomDefaultWeapon(current))}
              />
              <ActionBarSlot
                hotkey="T"
                item={flashlightItem}
                active={runtime.flashlightEnabled}
                emptyLabel="Фонарик"
                onClick={() => applyRuntimeAction((current) => toggleMysteryRoomFlashlight(current))}
              />
              <div className="h-14 w-px shrink-0 bg-site-text/10" />
              {Array.from({ length: 5 }, (_, index) => (
                <ActionBarSlot
                  key={`action-slot-${index}`}
                  hotkey={`${index + 1}`}
                  item={actionBarItems[index] ?? null}
                  active={actionBarItems[index]?.id === runtime.activeInventoryItemId}
                  emptyLabel="Пусто"
                  onClick={() => {
                    const item = actionBarItems[index];
                    if (item) {
                      applyRuntimeAction((current) => useMysteryRoomActionBarItem(current, item.id));
                    }
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        <aside className="flex min-h-0 flex-col gap-3 overflow-y-auto rounded-[24px] border border-site-border bg-site-bg/82 p-4">
          <div className="flex shrink-0 gap-1 rounded-[14px] border border-site-border bg-site-bg/70 p-1">
            <button
              type="button"
              onClick={() => setSidebarTab('summary')}
              className={`min-h-[40px] flex-1 rounded-[10px] px-2 py-2 text-center text-[0.68rem] font-semibold uppercase tracking-[0.12em] transition ${
                sidebarTab === 'summary' ? 'bg-site-text/12 text-site-text shadow-sm' : 'text-site-muted hover:text-site-text'
              }`}
            >
              Сводка
            </button>
            <button
              type="button"
              onClick={() => setSidebarTab('building')}
              className={`min-h-[40px] flex-1 rounded-[10px] px-2 py-2 text-center text-[0.68rem] font-semibold uppercase tracking-[0.12em] transition ${
                sidebarTab === 'building' ? 'bg-site-text/12 text-site-text shadow-sm' : 'text-site-muted hover:text-site-text'
              }`}
            >
              Стройка
            </button>
            <button
              type="button"
              onClick={() => setSidebarTab('ambientMusic')}
              className={`min-h-[40px] flex-1 rounded-[10px] px-2 py-2 text-center text-[0.68rem] font-semibold uppercase tracking-[0.12em] transition ${
                sidebarTab === 'ambientMusic' ? 'bg-site-text/12 text-site-text shadow-sm' : 'text-site-muted hover:text-site-text'
              }`}
            >
              Музыка окружения
            </button>
          </div>

          {sidebarTab === 'summary' ? (
            <>
              <Panel title="Безопасность">
                <div className="space-y-2 text-sm text-site-secondary">
                  <Metric label="World alarm" value={runtime.worldAlarm.state} />
                  <Metric label="Source" value={runtime.worldAlarm.sourceIncidentId ?? 'none'} />
                  {runtime.security.trespass ? (
                    <div className="rounded-[14px] border border-site-warning/20 bg-site-warning/8 px-3 py-2">
                      <div className="font-semibold text-site-warning">{runtime.security.trespass.zoneTitle}</div>
                      <div className="mt-1 text-[0.68rem] uppercase tracking-[0.16em] text-site-warning/80">
                        {runtime.security.trespass.escalatedAtMs === null ? 'warning' : 'alarm call'} · needs{' '}
                        {runtime.security.trespass.requiredAccess}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-[14px] border border-site-border bg-site-surface/[0.03] px-3 py-2 text-site-muted">
                      Нарушений допуска нет.
                    </div>
                  )}
                  {runtime.security.bodyReports.length > 0 ? (
                    runtime.security.bodyReports.map((report) => {
                      const victim = runtime.npcs.find((npc) => npc.id === report.npcId);
                      const reporter = runtime.npcs.find((npc) => npc.id === report.discoveredByNpcId);
                      return (
                        <div key={`${report.npcId}:${report.discoveredAtMs}`} className="rounded-[14px] border border-site-danger/18 bg-site-danger/8 px-3 py-2">
                          <div className="font-semibold text-site-danger">{victim?.name ?? report.npcId}</div>
                          <div className="mt-1 text-[0.68rem] uppercase tracking-[0.16em] text-site-danger/80">
                            found by {reporter?.name ?? report.discoveredByNpcId}
                          </div>
                          <div className="mt-2 text-site-muted">
                            guard {report.guardCalled ? 'called' : 'none'} · medic {report.medicCalled ? 'called' : 'none'}
                          </div>
                        </div>
                      );
                    })
                  ) : null}
                </div>
              </Panel>

              <Panel title="Глобальная миссия">
                <div className="space-y-2 text-sm text-site-secondary">
                  {runtime.globalMission.active ? (
                    <>
                      <div className="rounded-[14px] border border-site-accent/20 bg-site-accent/8 px-3 py-2">
                        <div className="text-[0.62rem] uppercase tracking-[0.16em] text-site-accent/85">{runtime.globalMission.title}</div>
                        <div className="mt-1 text-site-text">{runtime.globalMission.description}</div>
                      </div>
                      <Metric label="Прогресс" value={`${runtime.globalMission.current}/${runtime.globalMission.target}`} />
                      <Metric label="Статус" value={runtime.globalMission.completed ? 'Выполнена' : 'В процессе'} />
                      {runtime.zombieMode.enabled ? (
                        <>
                          <Metric label="Волна" value={`${Math.max(1, runtime.zombieMode.currentWave)}/${runtime.zombieMode.totalWaves}`} />
                          <Metric label="Врагов в волне" value={String(runtime.zombieMode.remainingToSpawn)} />
                        </>
                      ) : null}
                    </>
                  ) : (
                    <div className="rounded-[14px] border border-site-border bg-site-surface/[0.03] px-3 py-2 text-site-muted">
                      В этом запуске глобальная миссия не выбрана.
                    </div>
                  )}
                </div>
              </Panel>

              <Panel title="Эффекты">
                <div className="space-y-3 text-sm text-site-secondary">
                  <VolumeSlider
                    label="Выстрелы, шаги, тревога"
                    value={Math.round(clampMysteryRoomVolume(audioSettings.sfxVolume) * 100)}
                    onChange={(percent) =>
                      setAudioSettings((current) => ({
                        ...current,
                        sfxVolume: clampMysteryRoomVolume(percent / 100),
                      }))
                    }
                  />
                </div>
              </Panel>

              <Panel title="Чат смены">
                <div className="space-y-2 text-sm text-site-secondary">
                  <div className="max-h-44 space-y-2 overflow-auto pr-1">
                    {localChat.map((message) => (
                      <div key={message.id} className="rounded-[14px] border border-site-border bg-site-surface/[0.03] px-3 py-2">
                        <div className="text-[0.62rem] uppercase tracking-[0.16em] text-site-muted">{message.author}</div>
                        <div className="mt-1 text-site-text">{message.body}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={chatDraft}
                      onChange={(event) => setChatDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          sendLocalChatMessage();
                        }
                      }}
                      placeholder="Заметка смены..."
                      className="min-w-0 flex-1 rounded-[14px] border border-site-border bg-site-bg/60 px-3 py-2 text-sm text-site-text outline-none placeholder:text-site-muted focus:border-site-border"
                    />
                    <button
                      type="button"
                      onClick={sendLocalChatMessage}
                      className="rounded-[14px] bg-site-surface px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-site-muted transition hover:bg-site-raised"
                    >
                      send
                    </button>
                  </div>
                </div>
              </Panel>

              <Panel title="Освещение">
                <div className="space-y-2 text-sm text-site-secondary">
                  {runtime.lights.map((light) => {
                    const roomTitle = runtime.map.rooms.find((room) => room.id === light.zoneId)?.title ?? light.zoneId;
                    return (
                      <div key={light.id} className="flex items-center justify-between rounded-[14px] border border-site-border bg-site-surface/[0.03] px-3 py-2">
                        <span>{roomTitle}</span>
                        <span className={`text-[0.62rem] uppercase tracking-[0.16em] ${light.enabled ? 'text-site-accent' : 'text-site-muted'}`}>
                          {light.enabled ? 'on' : 'off'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Panel>

              <Panel title="События">
                <div className="space-y-2 text-sm text-site-secondary">
                  {runtime.events.length > 0 ? (
                    runtime.events.map((event, index) => (
                      <div key={`${event}:${index}`} className="rounded-[14px] border border-site-border bg-site-surface/[0.03] px-3 py-2">
                        {event}
                      </div>
                    ))
                  ) : (
                    <div className="rounded-[14px] border border-site-border bg-site-surface/[0.03] px-3 py-2 text-site-muted">
                      Пока без событий.
                    </div>
                  )}
                </div>
              </Panel>
            </>
          ) : sidebarTab === 'building' ? (
            <Panel title="Стройка">
              <BuildRecipePanel
                buildStates={buildStates}
                onBuild={(kind) => applyRuntimeAction((current) => buildMysteryRoomObject(current, kind))}
              />
            </Panel>
          ) : (
            <Panel title="Музыка окружения">
              <div className="space-y-3 text-sm text-site-secondary">
                <p className="leading-relaxed text-site-muted">
                  Громкость фоновых треков с карты (источники ambient). Настройка сохраняется в этом браузере.
                </p>
                <VolumeSlider
                  label="Громкость"
                  value={Math.round(clampMysteryRoomVolume(audioSettings.musicVolume) * 100)}
                  onChange={(percent) =>
                    setAudioSettings((current) => ({
                      ...current,
                      musicVolume: clampMysteryRoomVolume(percent / 100),
                    }))
                  }
                />
              </div>
            </Panel>
          )}
        </aside>
      </div>
    </section>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-[18px] border border-site-border bg-site-surface/[0.03] p-4">
      <div className="text-[0.68rem] uppercase tracking-[0.18em] text-site-muted">{title}</div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-[14px] border border-site-border bg-site-bg/60 px-3 py-2">
      <div className="text-[0.62rem] uppercase tracking-[0.16em] text-site-muted">{label}</div>
      <div className="mt-1 text-sm font-semibold text-site-text">{value}</div>
    </div>
  );
}

function VolumeSlider({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="block rounded-[14px] border border-site-border bg-site-bg/60 px-3 py-2">
      <div className="flex items-center justify-between text-[0.72rem] uppercase tracking-[0.14em] text-site-muted">
        <span>{label}</span>
        <span className="font-semibold text-site-text">{value}%</span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="mt-2 w-full accent-cyan-300"
      />
    </label>
  );
}

export type MysteryRoomInventoryHeroStats = {
  money: number;
  level: number;
  health: number;
  maxHealth: number;
  strengthKg: number;
  intelligence: number;
  speed: number;
  accessCount: number;
};

export function ActionBarSlot({
  hotkey,
  item,
  active,
  emptyLabel,
  onClick,
}: {
  hotkey: string;
  item: MysteryRoomInventoryItemState | null;
  active: boolean;
  emptyLabel: string;
  onClick: () => void;
}) {
  const accent = item ? inventoryItemAccent(item) : null;
  return (
    <button
      type="button"
      disabled={!item}
      title={item?.description ?? emptyLabel}
      onPointerDown={stopGameOverlayPropagation}
      onPointerUp={stopGameOverlayPropagation}
      onMouseDown={stopGameOverlayPropagation}
      onMouseUp={stopGameOverlayPropagation}
      onClick={(event) => {
        stopGameOverlayPropagation(event);
        onClick();
      }}
      className={`relative flex h-[68px] w-[68px] shrink-0 flex-col items-center justify-center rounded-[14px] border bg-site-bg/72 text-center transition ${active ? 'ring-2 ring-site-warning/90 ring-offset-2 ring-offset-site-bg' : ''} ${accent ? `${accent.border} ${accent.text} hover:-translate-y-0.5` : 'border-site-border text-site-muted opacity-70'}`}
    >
      <span className="absolute left-1.5 top-1.5 rounded-[8px] border border-site-border bg-site-surface/[0.06] px-1.5 py-0.5 text-[0.58rem] font-black text-site-text">
        {hotkey}
      </span>
      {item ? (
        <>
          <InventoryItemIcon item={item} className={`h-8 w-8 rounded-[10px] text-[0.62rem] ${accent?.icon ?? ''}`} />
          <span className="mt-1 block max-w-[58px] truncate text-[0.56rem] font-semibold text-site-text">{item.label}</span>
          {item.quantity > 1 ? (
            <span className="absolute bottom-1 right-1 rounded-[8px] border border-site-accent/30 bg-site-accent px-1 py-0.5 text-[0.56rem] font-black text-site-on-accent">
              {item.quantity}
            </span>
          ) : null}
        </>
      ) : (
        <span className="mt-5 block max-w-[58px] truncate text-[0.56rem] font-semibold">{emptyLabel}</span>
      )}
    </button>
  );
}

type MysteryRoomBuildState = ReturnType<typeof getMysteryRoomBuildState>;

function BuildRecipePanel({
  buildStates,
  onBuild,
}: {
  buildStates: Record<MysteryRoomBuildableKind, MysteryRoomBuildState>;
  onBuild: (kind: MysteryRoomBuildableKind) => void;
}) {
  return (
    <div className="space-y-2">
      {MYSTERY_ROOM_BUILDABLE_KINDS.map((kind) => (
        <BuildRecipeButton key={kind} kind={kind} buildState={buildStates[kind]} onClick={() => onBuild(kind)} />
      ))}
    </div>
  );
}

function BuildRecipeButton({
  kind,
  buildState,
  onClick,
}: {
  kind: MysteryRoomBuildableKind;
  buildState: MysteryRoomBuildState;
  onClick: () => void;
}) {
  const recipe = MYSTERY_ROOM_BUILD_RECIPES[kind];
  const cost = MYSTERY_ROOM_RESOURCE_KINDS
    .filter((resourceKind) => (recipe.cost[resourceKind] ?? 0) > 0)
    .map((resourceKind) => `${resourceShortLabel(resourceKind)}${recipe.cost[resourceKind]}`)
    .join(' ');
  return (
    <button
      type="button"
      disabled={!buildState.canBuild}
      title={`${recipe.label}: ${buildState.reason}`}
      onPointerDown={stopGameOverlayPropagation}
      onPointerUp={stopGameOverlayPropagation}
      onMouseDown={stopGameOverlayPropagation}
      onMouseUp={stopGameOverlayPropagation}
      onClick={(event) => {
        stopGameOverlayPropagation(event);
        onClick();
      }}
      className={`w-full rounded-[14px] border px-3 py-3 text-left transition ${
        buildState.canBuild
          ? 'border-site-success/35 bg-site-success/14 text-site-success hover:border-site-success/60'
          : 'border-site-border bg-site-surface/[0.04] text-site-muted'
      }`}
    >
      <span className="block text-sm font-black text-site-text">{recipe.label}</span>
      <span className="mt-1 block text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-site-muted">{cost}</span>
      <span className={`mt-2 block text-xs ${buildState.canBuild ? 'text-site-success/80' : 'text-site-muted'}`}>{buildState.reason}</span>
    </button>
  );
}

function resourceShortLabel(kind: keyof MysteryRoomResourceCounts) {
  if (kind === 'wood') return 'Д';
  if (kind === 'metal') return 'М';
  return 'К';
}

function InventoryOverlay({
  runtime,
  onClose,
  onMoveItem,
  activeItemId,
  onToggleActiveItem,
}: {
  runtime: MysteryRoomLocalRuntime;
  onClose: () => void;
  onMoveItem: (
    itemId: MysteryRoomInventoryItemState['id'],
    beforeItemId: MysteryRoomInventoryItemState['id'] | null,
  ) => void;
  activeItemId: MysteryRoomInventoryItemState['id'] | null;
  onToggleActiveItem: (itemId: MysteryRoomInventoryItemState['id']) => void;
}) {
  return (
    <div className="pointer-events-auto fixed left-1/2 top-1/2 z-30 flex h-[70vh] min-h-[420px] w-[70vw] min-w-[520px] max-h-[620px] max-w-[960px] -translate-x-1/2 -translate-y-1/2 flex-col rounded-[20px] border border-site-accent/18 bg-site-bg/68 shadow-[0_24px_76px_rgba(0,0,0,0.48)] backdrop-blur-xl">
      <div className="flex items-center justify-between border-b border-site-border px-4 py-3">
        <div>
          <div className="text-[0.68rem] uppercase tracking-[0.2em] text-site-accent/75">Inventory</div>
          <div className="mt-1 text-base font-black text-site-text">Снаряжение героя</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-[12px] border border-site-border bg-site-surface/[0.04] px-3 py-2 text-sm font-semibold text-site-text transition hover:border-site-border hover:bg-site-surface/[0.08]"
        >
          Закрыть
        </button>
      </div>

      <MysteryRoomInventoryPanel
        inventory={runtime.inventory}
        hero={{
          money: runtime.hero.money,
          level: runtime.hero.level,
          health: runtime.hero.health,
          maxHealth: runtime.hero.maxHealth,
          strengthKg: runtime.hero.strengthKg,
          intelligence: runtime.hero.intelligence,
          speed: runtime.hero.speed,
          accessCount: runtime.hero.accessLevels.length,
        }}
        activeItemId={activeItemId}
        flashlightEnabled={runtime.flashlightEnabled}
        onMoveItem={onMoveItem}
        onToggleActiveItem={onToggleActiveItem}
      />
    </div>
  );
}

export function MysteryRoomInventoryPanel({
  inventory,
  hero,
  activeItemId,
  flashlightEnabled,
  onMoveItem,
  onToggleActiveItem,
}: {
  inventory: MysteryRoomInventoryItemState[];
  hero: MysteryRoomInventoryHeroStats;
  activeItemId: MysteryRoomInventoryItemState['id'] | null;
  flashlightEnabled: boolean;
  onMoveItem?: (
    itemId: MysteryRoomInventoryItemState['id'],
    beforeItemId: MysteryRoomInventoryItemState['id'] | null,
  ) => void;
  onToggleActiveItem?: (itemId: MysteryRoomInventoryItemState['id']) => void;
}) {
  const sortedItems = [...inventory].sort((a, b) => a.slotIndex - b.slotIndex);
  const resourceItems = sortedItems.filter((item) => item.kind === 'resource');
  const equipmentItems = sortedItems.filter((item) => item.kind !== 'resource');
  const healthPercent = Math.max(0, Math.min(100, (hero.health / Math.max(1, hero.maxHealth)) * 100));
  const activeItem = equipmentItems.find((item) => item.id === activeItemId) ?? null;

  const handleContainerDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!onMoveItem) {
      return;
    }
    event.preventDefault();
    const itemId = readInventoryDragId(event);
    if (itemId) {
      onMoveItem(itemId, null);
    }
  };

  return (
    <div className="grid min-h-0 flex-1 gap-3 p-3 lg:grid-cols-[240px_minmax(0,1fr)]">
      <div className="flex min-h-0 flex-col rounded-[16px] border border-site-border bg-site-surface/[0.04] p-3">
        <div className="flex items-center justify-between text-sm">
          <span className="rounded-[12px] border border-site-warning/25 bg-site-warning/10 px-3 py-1 font-semibold text-site-warning">
            {hero.money} CR
          </span>
          <span className="rounded-[12px] border border-site-accent/25 bg-site-accent/10 px-3 py-1 font-semibold text-site-accent">
            LVL {hero.level}
          </span>
        </div>

        <div className="flex flex-1 items-center justify-center py-3">
          <InventoryHeroPreview />
        </div>

        <div className="space-y-2">
          <div>
            <div className="mb-1 flex items-center justify-between text-xs uppercase tracking-[0.16em] text-site-muted">
              <span>Здоровье</span>
              <span>
                {hero.health}/{hero.maxHealth}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-site-bg">
              <div className="h-full rounded-full bg-site-success" style={{ width: `${healthPercent}%` }} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Metric label="Сила" value={`${hero.strengthKg} кг`} />
            <Metric label="Интеллект" value={hero.intelligence} />
            <Metric label="Скорость" value={Math.round(hero.speed)} />
            <Metric label="Доступ" value={hero.accessCount} />
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-col rounded-[16px] border border-site-border bg-site-surface/[0.04] p-3">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-[0.68rem] uppercase tracking-[0.18em] text-site-muted">Items</div>
            <div className="mt-1 text-sm text-site-secondary">{equipmentItems.length} предметов · {resourceItems.length} ресурсов</div>
          </div>
          <div className="text-right text-sm font-semibold text-site-accent">
            {activeItem
              ? `Активен: ${activeItem.label}`
              : equipmentItems.some((item) => item.id === 'flashlight')
                ? `Фонарик ${flashlightEnabled ? 'включён' : 'выключен'}`
                : 'Фонарика нет'}
          </div>
        </div>

        <div
          className="min-h-0 flex-1 overflow-y-auto pr-1"
          onDragOver={(event) => onMoveItem ? event.preventDefault() : undefined}
          onDrop={handleContainerDrop}
        >
          {sortedItems.length > 0 ? (
            <div className="space-y-4">
              <section>
                <div className="mb-2 text-[0.68rem] font-black uppercase tracking-[0.16em] text-site-muted">Ресурсы</div>
                {resourceItems.length > 0 ? (
                  <div className="grid auto-rows-[84px] grid-cols-[repeat(auto-fill,minmax(92px,92px))] content-start gap-3">
                    {resourceItems.map((item) => (
                      <ResourceItemCell key={item.id} item={item} />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-[14px] border border-dashed border-site-border bg-site-bg/36 px-3 py-3 text-sm text-site-muted">
                    Ресурсов нет.
                  </div>
                )}
              </section>
              <section>
                <div className="mb-2 text-[0.68rem] font-black uppercase tracking-[0.16em] text-site-muted">Items</div>
                {equipmentItems.length > 0 ? (
                  <div className="grid auto-rows-[104px] grid-cols-[repeat(auto-fill,minmax(84px,84px))] content-start gap-3">
                    {equipmentItems.map((item) => (
                      <InventoryItemCell
                        key={item.id}
                        item={item}
                        active={item.id === activeItemId || (item.id === 'flashlight' && flashlightEnabled)}
                        onToggleActiveItem={onToggleActiveItem}
                        onMoveItem={onMoveItem}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-[14px] border border-dashed border-site-border bg-site-bg/36 px-3 py-3 text-sm text-site-muted">
                    Предметов нет.
                  </div>
                )}
              </section>
            </div>
          ) : (
            <div className="rounded-[16px] border border-dashed border-site-border bg-site-bg/44 px-4 py-6 text-sm text-site-muted">
              Инвентарь пуст.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InventoryHeroPreview() {
  return (
    <div className="relative h-44 w-32">
      <style jsx>{`
        @keyframes mr-anna-breath {
          0%,
          100% {
            transform: translateY(0) scaleX(1) scaleY(1);
          }
          50% {
            transform: translateY(-2px) scaleX(1.015) scaleY(0.988);
          }
        }
      `}</style>
      <div className="absolute inset-x-4 bottom-2 h-4 rounded-full bg-black/45 blur-md" />
      <div className="absolute inset-x-1 top-3 bottom-3 rounded-full bg-site-accent/8 blur-2xl" />
      <img
        src={MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN.menuUrl}
        alt={MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN.name}
        draggable={false}
        className="relative mx-auto h-full w-auto object-contain drop-shadow-[0_24px_34px_rgba(0,0,0,0.62)]"
        style={{
          animation: 'mr-anna-breath 3.2s ease-in-out infinite',
          transformOrigin: '50% 72%',
        }}
      />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full border border-site-warning/28 bg-site-bg/78 px-3 py-1 text-xs font-black text-site-warning shadow-[0_12px_26px_rgba(0,0,0,0.42)]">
        {MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN.name}
      </div>
    </div>
  );
}

function InventoryItemCell({
  item,
  active,
  onToggleActiveItem,
  onMoveItem,
}: {
  item: MysteryRoomInventoryItemState;
  active: boolean;
  onToggleActiveItem?: (itemId: MysteryRoomInventoryItemState['id']) => void;
  onMoveItem?: (
    itemId: MysteryRoomInventoryItemState['id'],
    beforeItemId: MysteryRoomInventoryItemState['id'] | null,
  ) => void;
}) {
  const accent = inventoryItemAccent(item);

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    if (!onMoveItem) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const itemId = readInventoryDragId(event);
    if (itemId && itemId !== item.id) {
      onMoveItem(itemId, item.id);
    }
  };

  return (
    <button
      type="button"
      draggable={Boolean(onMoveItem)}
      title={item.description ?? item.label}
      onDragStart={(event) => {
        if (!onMoveItem) {
          event.preventDefault();
          return;
        }
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('application/x-mr-item-id', item.id);
        event.dataTransfer.setData('text/plain', item.id);
      }}
      onDragOver={(event) => onMoveItem ? event.preventDefault() : undefined}
      onDrop={handleDrop}
      onClick={() => onToggleActiveItem?.(item.id)}
      className={`relative flex h-[104px] w-[84px] flex-col items-center overflow-visible rounded-[14px] text-center transition hover:-translate-y-0.5 ${active ? 'ring-2 ring-site-warning/90 ring-offset-2 ring-offset-site-bg' : ''} ${accent.text}`}
    >
      <div className={`flex h-[78px] w-[78px] items-center justify-center rounded-[14px] border bg-site-bg/72 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.03)] ${accent.border}`}>
        <InventoryItemIcon item={item} className={`h-[72px] w-[72px] rounded-[12px] text-xs ${accent.icon}`} />
      </div>
      <span className="mt-1 block w-[94px] max-w-[94px] truncate text-[0.58rem] font-semibold text-site-text">{item.label}</span>
      {item.quantity > 1 ? (
        <span className="absolute right-1.5 top-[54px] rounded-[9px] border border-site-accent/30 bg-site-accent px-1.5 py-0.5 text-[0.62rem] font-black text-site-on-accent">
          {item.quantity}
        </span>
      ) : null}
      {active ? (
        <span className="absolute left-1.5 top-1.5 rounded-[8px] border border-site-warning/40 bg-site-warning px-1.5 py-0.5 text-[0.55rem] font-black text-site-on-accent">
          ON
        </span>
      ) : null}
    </button>
  );
}

function ResourceItemCell({ item }: { item: MysteryRoomInventoryItemState }) {
  const accent = inventoryItemAccent(item);
  return (
    <div
      title={item.description ?? item.label}
      className={`relative flex h-[84px] w-[92px] flex-col items-center rounded-[14px] border bg-site-bg/58 px-2 py-2 text-center ${accent.border} ${accent.text}`}
    >
      <InventoryItemIcon item={item} className={`h-10 w-10 rounded-[10px] text-[0.62rem] ${accent.icon}`} />
      <span className="mt-1 block w-full truncate text-[0.58rem] font-semibold text-site-text">{item.label}</span>
      <span className="absolute right-1.5 top-1.5 rounded-[9px] border border-site-success/30 bg-site-success px-1.5 py-0.5 text-[0.62rem] font-black text-site-on-accent">
        {item.quantity}
      </span>
    </div>
  );
}

function readInventoryDragId(event: DragEvent<HTMLElement>): MysteryRoomInventoryItemState['id'] | null {
  const value = event.dataTransfer.getData('application/x-mr-item-id') || event.dataTransfer.getData('text/plain');
  const normalized = value.trim();
  return normalized ? normalized as MysteryRoomInventoryItemState['id'] : null;
}

function InventoryItemIcon({ item, className }: { item: MysteryRoomInventoryItemState; className?: string }) {
  const classes = `flex items-center justify-center overflow-hidden border font-black ${className ?? ''}`;
  return (
    <span className={classes} title={item.iconAssetName ?? item.icon}>
      {item.iconAssetUrl ? (
        <img src={item.iconAssetUrl} alt="" className="h-full w-full object-cover" draggable={false} />
      ) : (
        item.icon
      )}
    </span>
  );
}

function inventoryItemAccent(item: MysteryRoomInventoryItemState) {
  if (item.kind === 'resource') {
    if (item.id.includes('wood')) {
      return {
        border: 'border-site-success/28 hover:border-site-success/50',
        icon: 'border-site-success/25 bg-site-success/14 text-site-success',
        text: 'text-site-success',
      };
    }
    if (item.id.includes('metal')) {
      return {
        border: 'border-site-border/28 hover:border-site-border/50',
        icon: 'border-site-border/25 bg-site-raised/14 text-site-text',
        text: 'text-site-text',
      };
    }
    return {
      border: 'border-site-border/30 hover:border-site-border/50',
      icon: 'border-site-border/25 bg-site-raised/14 text-site-text',
      text: 'text-site-text',
    };
  }
  if (item.kind === 'access_card') {
    return {
      border: 'border-site-accent/35 hover:border-site-accent/60',
      icon: 'border-site-accent/25 bg-site-accent/18 text-site-accent',
      text: 'text-site-accent',
    };
  }
  if (item.id === 'flashlight') {
    return {
      border: 'border-site-warning/35 hover:border-site-warning/60',
      icon: 'border-site-warning/25 bg-site-warning/18 text-site-warning',
      text: 'text-site-warning',
    };
  }

  if (item.isWeapon) {
    return {
      border: 'border-site-warning/30 hover:border-site-warning/55',
      icon: 'border-site-warning/25 bg-site-warning/14 text-site-warning',
      text: 'text-site-warning',
    };
  }

  return {
    border: 'border-site-accent/24 hover:border-site-accent/45',
    icon: 'border-site-accent/25 bg-site-accent/12 text-site-accent',
    text: 'text-site-accent',
  };
}

export function buildMysteryRoomTextState(
  runtime: MysteryRoomLocalRuntime,
  localChat: LocalChatMessage[] = [],
  _audio: unknown = null,
  _camera: unknown = null,
) {
  const heroRoom = runtime.map.rooms.find((room) => room.id === runtime.hero.zoneId);
  const resources = getMysteryRoomResourceCounts(runtime);
  const buildings = runtime.map.customObjects
    .filter((object) => object.tags?.includes('building'))
    .map((object) => ({
      id: object.id,
      kind: object.tags.find((tag) => tag === 'barricade' || tag === 'resource_crate') ?? object.kind,
      label: object.label,
      x: Math.round(object.rect.x + object.rect.width / 2),
      y: Math.round(object.rect.y + object.rect.height / 2),
    }));

  return {
    coordinateSystem: 'world pixels, origin top-left, x right, y down',
    mode: 'local-core-slice',
    gameplay: runtime.gameplay,
    globalMission: runtime.globalMission,
    gameStatus: runtime.gameStatus,
    time: {
      elapsedMs: Math.round(runtime.elapsedMs),
    },
    map: {
      id: runtime.map.id,
      title: runtime.map.title,
    },
    player: {
      x: Math.round(runtime.hero.position.x),
      y: Math.round(runtime.hero.position.y),
      movementMode: runtime.hero.movementMode,
      animationState: runtime.hero.animationState,
    },
    playerStatus: {
      zoneTitle: heroRoom?.title ?? 'unknown',
      alertState: runtime.hero.alertState,
      alive: runtime.hero.alive,
      healthPercent: Math.round((runtime.hero.health / Math.max(1, runtime.hero.maxHealth)) * 100),
      respawnSeconds: runtime.respawn
        ? Math.max(0, Math.ceil((runtime.respawn.readyAtMs - runtime.elapsedMs) / 1000))
        : 0,
      hasFlashlight: hasInventoryFlashlight(runtime),
      flashlightEnabled: runtime.flashlightEnabled,
    },
    publicCounts: {
      openDoors: runtime.doors.filter((door) => door.state === 'open').length,
      enabledLights: runtime.lights.filter((light) => light.enabled).length,
      visibleNpcCount: runtime.visibility
        ? runtime.npcs.filter((npc) => isCircleVisible(runtime.visibility, npc.position, npc.radius)).length
        : 0,
      incidents: runtime.incidents.length,
      activeInventorySlots: runtime.inventory.filter((item) => (item.quantity ?? 1) > 0).length,
      nearbyActionAvailable: getPrimaryLocalAction(runtime) !== null,
    },
    combat: {
      projectiles: runtime.projectiles.length,
      effects: runtime.impactEffects.length,
      bloodEffects: runtime.impactEffects.filter((effect) => effect.kind.startsWith('blood_')).length,
    },
    resources,
    buildings,
    worldAlarm: {
      state: runtime.worldAlarm.state,
    },
    security: {
      trespassActive: runtime.security.trespass !== null,
      trespassEscalated: runtime.security.trespass ? runtime.security.trespass.escalatedAtMs !== null : false,
      bodyReportCount: runtime.security.bodyReports.length,
    },
    chat: localChat.map((message) => ({
      author: message.author,
      body: message.body,
      createdAtMs: Math.round(message.createdAtMs),
    })),
  };
}

function clampMysteryRoomVolume(value: number) {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.min(1, value));
}

function readMysteryRoomAudioSettings(): MysteryRoomAudioSettings {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_MYSTERY_ROOM_AUDIO_SETTINGS };
  }
  try {
    const raw = window.localStorage.getItem(MYSTERY_ROOM_AUDIO_SETTINGS_STORAGE_KEY);
    if (!raw) {
      return { ...DEFAULT_MYSTERY_ROOM_AUDIO_SETTINGS };
    }
    const parsed = JSON.parse(raw) as Partial<MysteryRoomAudioSettings>;
    return {
      musicVolume: clampMysteryRoomVolume(
        typeof parsed.musicVolume === 'number' ? parsed.musicVolume : DEFAULT_MYSTERY_ROOM_AUDIO_SETTINGS.musicVolume,
      ),
      sfxVolume: clampMysteryRoomVolume(
        typeof parsed.sfxVolume === 'number' ? parsed.sfxVolume : DEFAULT_MYSTERY_ROOM_AUDIO_SETTINGS.sfxVolume,
      ),
    };
  } catch {
    return { ...DEFAULT_MYSTERY_ROOM_AUDIO_SETTINGS };
  }
}
